import React, { forwardRef, useImperativeHandle } from 'react';
import { Add } from '@mui/icons-material';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import type { VendingSalesDynamicFormRHFNewProps } from '../../types/vendingSalesFormTypes';
import useVendingSalesDynamicFormLogic from '../../features/audit/fixOcr/hooks/useVendingSalesDynamicFormLogic';
import AuditDynamicFormActions from './AuditDynamicFormActions';
import AuditDynamicFormErrorDialog from './AuditDynamicFormErrorDialog';
import AuditDynamicFormTopBar from './AuditDynamicFormTopBar';
import VendingInventoryMultiPackRows from './VendingInventoryMultiPackRows';
import VendingSalesFormHeader from './VendingSalesFormHeader';
import VendingSalesFormLineRow from './VendingSalesFormLineRow';
import VendingSalesFormSync from './VendingSalesFormSync';
import VendingSalesFormTableHeader from './VendingSalesFormTableHeader';
import VendingSalesFormTotals from './VendingSalesFormTotals';
import VendingInventoryFormTotalsRow from './VendingInventoryFormTotalsRow';

const VendingSalesDynamicFormRHFNew = forwardRef<any, VendingSalesDynamicFormRHFNewProps>(
  (
    {
      initialValues,
      onSubmit,
      isSubmitting,
      savingAction,
      auditStatus,
      onFormDataUpdate,
      onSwitchModes,
      hoveredRowIndex,
      onRowHover,
      storeName,
      gamesData,
      reportType,
      queueId,
      onRevertToReady,
      vendingInventoryNewPackKeys,
      vendingInventoryCompletedPacks,
      storeLocations,
      showTopBar = true,
      showConfidenceChip = true,
      showJsonToggle = true,
      showRejectAction = true,
      showRevertAction = true,
      submitButtonLabel = 'Process',
    },
    ref,
  ) => {
    const {
      control,
      handleSubmit,
      errors,
      setValue,
      getValues,
      fields,
      errorPopupOpen,
      errorMessage,
      handleAppend,
      handleInsert,
      handleRemove,
      handleSwitchToJson,
      handleReject,
      handleCloseErrorDialog,
      handleFormSubmit,
      handleSubmitError,
    } = useVendingSalesDynamicFormLogic({
      initialValues,
      onSubmit,
      onSwitchModes,
      gamesData,
      reportType,
    });

    useImperativeHandle(ref, () => ({
      getValues,
      setValue,
    }), [getValues, setValue]);

    const watchedLines = useWatch({
      control,
      name: 'parsed_data.final_json.lines',
    });
    const watchedShiftDate = useWatch({
      control,
      name: 'shift_date',
    });

    const effectiveLines = Array.isArray(watchedLines)
      ? watchedLines
      : (initialValues?.parsed_data?.final_json?.lines || []);

    const newPackKeySet = React.useMemo(() => (
      new Set(Array.isArray(vendingInventoryNewPackKeys) ? vendingInventoryNewPackKeys : [])
    ), [vendingInventoryNewPackKeys]);

    const completedPacks = React.useMemo(() => (
      Array.isArray(vendingInventoryCompletedPacks) ? vendingInventoryCompletedPacks : []
    ), [vendingInventoryCompletedPacks]);

    return (
      <form onSubmit={handleSubmit(handleFormSubmit as any, handleSubmitError as any)}>
        <VendingSalesFormSync control={control} getValues={getValues} onFormDataUpdate={onFormDataUpdate} />

        {showTopBar && (
          <AuditDynamicFormTopBar
            storeName={storeName}
            initialValues={initialValues}
            onSwitchToJson={handleSwitchToJson}
            showConfidenceChip={showConfidenceChip}
            showJsonToggle={showJsonToggle}
          />
        )}

        <Box
          mt={0.5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            minWidth: 0,
            width: '100%',
          }}
        >
          <Box
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              maxWidth: 720,
              minWidth: 0,
              mx: 'auto',
              px: { xs: 2, sm: 3, lg: 5 },
              pt: 4,
              pb: 3,
              border: '1px solid #d0aa6f',
              borderRadius: 0,
              bgcolor: '#fffaf0',
              color: '#2b2825',
              boxShadow: '0 10px 24px rgba(80, 56, 26, 0.08)',
              fontFamily: '"Courier Prime", "Courier New", monospace',
            }}
          >
            <VendingSalesFormHeader control={control} errors={errors} reportType={reportType} storeLocations={storeLocations} />

            <Box
              sx={{
                borderTop: '2px solid #b98542',
                mt: 2.25,
                pt: 1.75,
              }}
            />

            <VendingSalesFormTableHeader reportType={reportType} />

            {fields.map((field: any, idx: number) => {
              const rowErrors: any =
                ((errors as any)?.parsed_data?.final_json?.lines || [])[idx] || {};
              const line = effectiveLines[idx];
              const isNewPack = reportType === 'vending_inventory'
                && line?.bin != null
                && line?.game != null
                && newPackKeySet.has(`${String(line.bin).trim()}:${String(line.game).trim()}`);

              return reportType === 'vending_inventory' ? (
                <VendingInventoryMultiPackRows
                  key={`vending-sales-line-row-${field.id}-${idx}`}
                  field={field}
                  idx={idx}
                  line={line}
                  linePathBase={`parsed_data.final_json.lines.${idx}`}
                  rowErrors={rowErrors}
                  setValue={setValue}
                  remove={handleRemove}
                  onInsert={handleInsert}
                  gamesData={gamesData}
                  reportType={reportType}
                  queueId={queueId}
                  reportDate={typeof watchedShiftDate === 'string' ? watchedShiftDate : ''}
                  auditStatus={auditStatus}
                  hoveredRowIndex={hoveredRowIndex}
                  onRowHover={onRowHover}
                  isNewPack={isNewPack}
                />
              ) : (
                <VendingSalesFormLineRow
                  key={`vending-sales-line-row-${field.id}-${idx}`}
                  field={field}
                  idx={idx}
                  line={line}
                  control={control}
                  rowErrors={rowErrors}
                  setValue={setValue}
                  remove={handleRemove}
                  onInsert={handleInsert}
                  gamesData={gamesData}
                  reportType={reportType}
                  queueId={queueId}
                  reportDate={typeof watchedShiftDate === 'string' ? watchedShiftDate : ''}
                  auditStatus={auditStatus}
                  hoveredRowIndex={hoveredRowIndex}
                  onRowHover={onRowHover}
                  isNewPack={isNewPack}
                />
              );
            })}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'transparent',
                cursor: 'pointer',
                opacity: 1,
                height: 20,
              }}
            >
              <Box
                className="add-btn"
                sx={{
                  width: 'calc( 100% - 20px )',
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ p: 0, width: 20, height: 20 }}
                  onClick={handleAppend}
                >
                  <Add sx={{ fontSize: 20 }} />
                </IconButton>
                <Divider
                  sx={{
                    borderColor:
                      hoveredRowIndex === effectiveLines.length ? '#3a3128' : '#d6b982',
                    borderWidth: '0.5px',
                    borderStyle: 'dashed',
                    width: '100%',
                    transition: 'all 0.1s ease-in-out',
                    boxShadow:
                      hoveredRowIndex === effectiveLines.length
                        ? '0 0 8px rgba(185, 133, 66, 0.3)'
                        : 'none',
                  }}
                />
              </Box>
            </Box>

            {reportType === 'vending_inventory' && (
              <VendingInventoryFormTotalsRow control={control} gamesData={gamesData} />
            )}

            {reportType === 'vending_sales' && (
              <VendingSalesFormTotals control={control} errors={errors} gamesData={gamesData} />
            )}

            {reportType === 'vending_inventory' && completedPacks.length > 0 && (
              <Box sx={{ mt: 0.75, px: 0.5 }}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid rgba(255, 152, 0, 0.9)',
                    bgcolor: 'rgba(255, 152, 0, 0.08)',
                    px: 1,
                    py: 0.75,
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'rgba(255, 152, 0, 1)', mb: 1 }}>
                    Assumed Completed Packs
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '72px 120px 90px 110px',
                      columnGap: 1,
                      alignItems: 'center',
                      px: 0.5,
                      mb: 0.5,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'rgba(34, 43, 69, 0.8)' }}>
                      Bin
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'rgba(34, 43, 69, 0.8)' }}>
                      GameId
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'rgba(34, 43, 69, 0.8)' }}>
                      Qty
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'rgba(34, 43, 69, 0.8)' }}>
                      Value
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 0.5, borderColor: 'rgba(255, 152, 0, 0.35)' }} />

                  {completedPacks.map((pack, index) => (
                    <React.Fragment key={pack.key}>
                      {index > 0 && <Divider sx={{ my: 0.6, borderColor: 'rgba(255, 152, 0, 0.25)' }} />}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '72px 120px 90px 110px',
                          columnGap: 1,
                          alignItems: 'center',
                          px: 0.5,
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>
                          {pack.bin}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>
                          {pack.gameId}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>
                          {pack.quantity ?? '-'}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>
                          ${pack.value ?? '-'}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <AuditDynamicFormActions
          isSubmitting={isSubmitting}
          savingAction={savingAction}
          auditStatus={auditStatus}
          onReject={handleReject}
          onRevertToReady={onRevertToReady}
          showRejectAction={showRejectAction}
          showRevertAction={showRevertAction}
          submitButtonLabel={submitButtonLabel}
        />

        <AuditDynamicFormErrorDialog
          open={errorPopupOpen}
          errorMessage={errorMessage}
          onClose={handleCloseErrorDialog}
        />
      </form>
    );
  },
);

export default VendingSalesDynamicFormRHFNew;
